import type { PaginatedResponse } from "@/types/api";
import type {
  CreateFulfillmentPayload,
  OnlineOrder,
  OnlineOrderShipment,
  OnlineOrderStatus,
  OnlineOrdersPage,
  OnlineOrdersQuery,
} from "@/types/onlineOrders";
import { apiUrl } from "@/utils/url";
import { HttpClient, HttpError } from "../HttpClient";
import { isAbortError, toServiceError } from "../errors";
import { OrderNotShippableError, ShipmentUnconfirmedError } from "./errors";
import type {
  IOnlineOrdersService,
  OnlineOrdersStore,
} from "./IOnlineOrdersService";
import type { ShipmentDTO, ShipmentView, ShopifyOrderView } from "./types";

const STATUSES: Record<ShopifyOrderView["statusCode"], OnlineOrderStatus> = {
  RECEIVED: "PENDING",
  FULFILLED: "FULFILLED",
  CANCELLED: "CANCELLED",
};

/*
 * What the API filters by. "Com erro" is a received order whose shipment
 * failed, which it cannot select, so it shows under "Por enviar".
 */
const STATUS_FILTERS: Partial<
  Record<OnlineOrderStatus, ShopifyOrderView["statusCode"]>
> = {
  PENDING: "RECEIVED",
  FULFILLED: "FULFILLED",
  CANCELLED: "CANCELLED",
};

/*
 * The stores that ship online orders, by `storeCode` (ids differ between
 * environments). Hardcoded until the backend has a store eligibility field.
 */
const ENABLED_STORE_CODES: ReadonlySet<string> = new Set([
  "3218", // ZU Matosinhos
]);

/* The shipment went: Shopify confirmed it, whatever the carrier did next. */
const SENT_SHIPMENTS = new Set<ShipmentView["statusCode"]>([
  "SHIPPED",
  "IN_TRANSIT",
  "DELIVERED",
]);

/*
 * A shipment waits for Shopify's stock feedback, which the API retries before
 * answering, so it gets far longer than the default before it counts as
 * unanswered.
 */
const SHIPMENT_TIMEOUT_MS = 60 * 1000;

/** Why an order shows as an error; the API keeps the reason to itself. */
const FAILED_SHIPMENT_MESSAGE =
  "A loja online não confirmou o envio. Contacte a equipa de Suporte.";
const CANCELLED_SHIPMENT_MESSAGE =
  "O envio foi cancelado e a encomenda não pode ser enviada de novo. Contacte a equipa de Suporte.";
const PENDING_SHIPMENT_MESSAGE =
  "O envio ainda não foi confirmado pela loja online. Se continuar assim, contacte a equipa de Suporte.";

const errorCodeOf = (error: unknown): string | undefined =>
  error instanceof HttpError &&
  typeof error.responseData?.errorCode === "string"
    ? error.responseData.errorCode
    : undefined;

/**
 * Whether no answer came, so the shipment may still have gone: a timeout
 * (ours, or a gateway's), a dropped connection, or a proxy's 502/503 that
 * never reached the API's own answer (it would carry an error code).
 */
const isUnanswered = (error: unknown) =>
  error instanceof TypeError ||
  (error instanceof HttpError &&
    (error.status === 408 ||
      error.status === 504 ||
      ((error.status === 502 || error.status === 503) &&
        errorCodeOf(error) === undefined)));

/**
 * Refusals about the order rather than what was packed: a recorded but
 * unconfirmed shipment, one already there, or an order no longer received.
 */
const isNotShippable = (error: unknown) => {
  const code = errorCodeOf(error);
  return (
    code !== undefined &&
    (code.startsWith("SHOPIFY_STOCK_FEEDBACK_") ||
      code === "SHIPMENT_ORDER_DUPLICATE_SHIPMENT" ||
      code === "SHIPMENT_ORDER_NOT_RECEIVED" ||
      code === "ENTITY_NOT_FOUND")
  );
};

const mapShipment = (shipment: ShipmentView): OnlineOrderShipment => ({
  id: shipment.id,
  createdAt: shipment.registeredOn,
  boxes: shipment.boxCount,
  lines: shipment.items.map(item => ({
    lineId: item.shopifyOrderItemId,
    quantity: item.quantity,
  })),
});

/**
 * An order ships once, and any shipment blocks another, so a received order
 * with a shipment that has not gone is stuck: it shows as an error, failed or
 * still unconfirmed. A sent order carries its shipment, and each of its lines
 * the units that shipment carried.
 */
const mapOrder = (
  { shippingInfo: shipping, items, ...order }: ShopifyOrderView,
  shipment: ShipmentView | undefined,
): OnlineOrder => {
  const sent =
    shipment && SENT_SHIPMENTS.has(shipment.statusCode) ? shipment : undefined;
  const stuck =
    order.statusCode === "RECEIVED" && shipment !== undefined && !sent;

  return {
    id: order.id,
    number: order.externalRefNo,
    createdAt: order.registeredOn,
    /* A sent shipment wins over an order status that has not caught up. */
    status: sent ? "FULFILLED" : stuck ? "ERROR" : STATUSES[order.statusCode],
    customer: {
      name: shipping.clientName,
      email: shipping.clientEmail,
      phone: shipping.clientCellphone ?? "",
    },
    shippingAddress: {
      name: shipping.clientName,
      line1: shipping.addressLine,
      postalCode: shipping.postalCode,
      city: shipping.city,
      province: shipping.province ?? undefined,
      country: shipping.country,
    },
    deliveryType: shipping.deliveryType,
    lines: items.map(item => ({
      id: item.id,
      sku: item.sku,
      /* Older transfers have no name; the SKU still identifies the product. */
      name: item.name || `SKU ${item.sku}`,
      quantity: item.quantity,
      fulfilledQuantity:
        sent?.items.find(
          ({ shopifyOrderItemId }) => shopifyOrderItemId === item.id,
        )?.quantity ?? 0,
    })),
    shipment: sent ? mapShipment(sent) : undefined,
    errorMessage: stuck
      ? shipment.statusCode === "PENDING"
        ? PENDING_SHIPMENT_MESSAGE
        : shipment.statusCode === "CANCELLED"
          ? CANCELLED_SHIPMENT_MESSAGE
          : FAILED_SHIPMENT_MESSAGE
      : undefined,
  };
};

/**
 * The Shopify transfers a store processes, with their lines and the shipment
 * made for each. It ships them, once each: the API rejects a second shipment
 * even after a failed one, and has no transport labels yet.
 */
export class OnlineOrdersService implements IOnlineOrdersService {
  readonly supportsShipping = true;
  readonly supportsLabels = false;
  readonly supportsRetry = false;
  readonly statusFilters: readonly OnlineOrderStatus[] = [
    "PENDING",
    "FULFILLED",
    "CANCELLED",
  ];
  private readonly httpClient = new HttpClient({ baseURL: apiUrl });

  async isEnabledForStore({
    id,
    storeCode,
  }: OnlineOrdersStore): Promise<boolean> {
    return id > 0 && ENABLED_STORE_CODES.has(storeCode);
  }

  /* Received orders are the ones not shipped, stuck ones included. */
  async countToFulfil(storeId: number, signal?: AbortSignal): Promise<number> {
    const result = await this.httpClient.post<
      PaginatedResponse<ShopifyOrderView>
    >(
      `/shopify/orders/store/${storeId}/search?page=0&size=1`,
      { statusCode: "RECEIVED" },
      { signal },
    );
    return result.page.totalElements;
  }

  async getOrders(
    storeId: number,
    query: OnlineOrdersQuery,
    signal?: AbortSignal,
  ): Promise<OnlineOrdersPage> {
    const statusCode =
      query.status === undefined ? undefined : STATUS_FILTERS[query.status];
    /* Ignoring a filter the API lacks would list the wrong orders. */
    if (query.status !== undefined && statusCode === undefined) {
      throw new Error("Este filtro por estado ainda não está disponível.");
    }
    const params = new URLSearchParams({
      page: String(query.page - 1),
      size: String(query.pageSize),
    });
    /* "#1042" as the list shows it finds the order "1042". */
    const searchTerm =
      query.search?.trim().replace(/^#(?=\d+$)/, "") || undefined;

    try {
      const result = await this.httpClient.post<
        PaginatedResponse<ShopifyOrderView>
      >(
        `/shopify/orders/store/${storeId}/search?${params}`,
        { searchTerm, startTime: query.from, endTime: query.to, statusCode },
        { signal },
      );
      const shipments = await this.getShipments(
        storeId,
        result.content,
        signal,
      );

      return {
        items: result.content.map(order =>
          mapOrder(order, shipments.get(order.id)),
        ),
        total: result.page.totalElements,
        counts: null,
      };
    } catch (error) {
      if (isAbortError(error)) throw error;
      throw toServiceError(
        error,
        "Não foi possível carregar as encomendas online. Atualize a página e tente novamente.",
      );
    }
  }

  /**
   * The shipment of each order that can have one, by order id: one request
   * per order, so the cost stays the page size however old the orders are.
   */
  private async getShipments(
    storeId: number,
    orders: ShopifyOrderView[],
    signal?: AbortSignal,
  ): Promise<Map<number, ShipmentView>> {
    const shipments = await Promise.all(
      orders
        .filter(order => order.statusCode !== "CANCELLED")
        .map(async order => {
          /* At most one per order, so the first is the order's shipment. */
          const [shipment] = await this.httpClient.get<ShipmentView[]>(
            `/shipments/store/${storeId}/shopify-order/${order.id}`,
            { signal },
          );
          return [order.id, shipment] as const;
        }),
    );

    return new Map(
      shipments.filter(
        (entry): entry is readonly [number, ShipmentView] =>
          entry[1] !== undefined,
      ),
    );
  }

  /**
   * Ships the order and answers with it as it now stands, without reading it
   * back: the API answers with the new shipment's location only, and the page
   * reloads the list once it has gone.
   */
  async createFulfillment(
    storeId: number,
    order: OnlineOrder,
    { lines, boxes }: CreateFulfillmentPayload,
    signal?: AbortSignal,
  ): Promise<OnlineOrder> {
    const shipment: ShipmentDTO = {
      storeId,
      shopifyOrderId: order.id,
      boxCount: boxes,
      items: lines.map(({ lineId, quantity }) => ({
        shopifyOrderItemId: lineId,
        quantity,
      })),
    };

    let shipmentId: number;
    try {
      shipmentId = await this.httpClient.post<number>(
        "/shipments",
        shipment,
        { signal, timeout: SHIPMENT_TIMEOUT_MS },
        true,
      );
    } catch (error) {
      if (isAbortError(error)) throw error;
      if (isUnanswered(error)) {
        throw new ShipmentUnconfirmedError({ cause: error });
      }
      const translated = toServiceError(
        error,
        "Não foi possível criar o envio. Tente novamente. Se o problema continuar, contacte a equipa de Suporte.",
      );
      throw isNotShippable(error)
        ? new OrderNotShippableError(translated.message)
        : translated;
    }

    const sent = new Map(lines.map(line => [line.lineId, line.quantity]));
    return {
      ...order,
      status: "FULFILLED",
      errorMessage: undefined,
      lines: order.lines.map(line => ({
        ...line,
        fulfilledQuantity: sent.get(line.id) ?? 0,
      })),
      shipment: {
        id: shipmentId,
        createdAt: new Date().toISOString(),
        boxes,
        lines,
      },
    };
  }

  async getShippingLabels(
    _storeId: number,
    _orderId: number,
    _signal?: AbortSignal,
  ): Promise<Blob> {
    throw new Error("As guias de transporte ainda não estão disponíveis.");
  }
}
